import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  RefreshCw,
  MoreHorizontal,
  Ban,
  CheckCircle2,
  RotateCcw,
  Eye,
  Loader2,
  Users,
  KeyRound,
  Copy,
  Check,
  ClipboardList,
  FlaskConical,
  Sparkles,
  Download,
  AlertTriangle,
  Gift,
  XCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtRelative(iso) {
  if (!iso) return "never";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return fmtDate(iso);
  } catch {
    return iso;
  }
}

function PlanBadge({ plan, planName }) {
  const colorMap = {
    free: "bg-[color:var(--surface-2)] text-[color:var(--text-2)] border-[color:var(--border-subtle)]",
    starter: "bg-[color:rgba(77,183,255,0.10)] text-[color:var(--info)] border-[color:rgba(77,183,255,0.35)]",
    pro: "bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)] border-[color:rgba(27,168,159,0.35)]",
    business: "bg-[color:rgba(255,138,61,0.10)] text-[color:var(--accent-warm)] border-[color:rgba(255,138,61,0.35)]",
  };
  return (
    <span
      className={
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium " +
        (colorMap[plan] || colorMap.free)
      }
    >
      {planName || plan}
    </span>
  );
}

function StatusBadge({ status, suspended }) {
  if (suspended) {
    return (
      <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium bg-[color:rgba(255,92,108,0.10)] text-[color:var(--danger)] border-[color:rgba(255,92,108,0.35)]">
        Suspended
      </span>
    );
  }
  const map = {
    active: "bg-[color:rgba(47,231,161,0.10)] text-[color:var(--success)] border-[color:rgba(47,231,161,0.35)]",
    canceled: "bg-[color:rgba(127,138,163,0.10)] text-[color:var(--text-2)] border-[color:var(--border-subtle)]",
    past_due: "bg-[color:rgba(255,138,61,0.10)] text-[color:var(--accent-warm)] border-[color:rgba(255,138,61,0.35)]",
  };
  return (
    <span
      className={
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium " +
        (map[status] || map.active)
      }
    >
      {status || "active"}
    </span>
  );
}

function PlatformDots({ items }) {
  if (!items?.length) return <span className="text-[11px] text-[color:var(--text-3)]">—</span>;
  const colors = {
    Facebook: "#1877F2",
    Instagram: "#E4405F",
    X: "#E2E8F0",
    TikTok: "#A78BFA",
    LinkedIn: "#0A66C2",
  };
  return (
    <div className="flex items-center gap-1.5">
      {items.map((c) => (
        <div
          key={c.platform}
          title={`${c.platform} (${c.mode})`}
          className="h-2 w-2 rounded-full"
          style={{ background: colors[c.platform] || "#7F8AA3", opacity: c.mode === "demo" ? 0.5 : 1 }}
        />
      ))}
    </div>
  );
}

export default function AdminCustomersPage() {
  // Default scope from ?scope=… so the AdminOverview "Review test accounts" link can deep-link.
  const initialScope = (() => {
    if (typeof window === "undefined") return "real";
    const s = new URLSearchParams(window.location.search).get("scope");
    return s === "test" || s === "all" ? s : "real";
  })();
  const [data, setData] = useState({ items: [], total: 0, applied_scope: initialScope });
  const [q, setQ] = useState("");
  const [scope, setScope] = useState(initialScope);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [resetLink, setResetLink] = useState(null); // { email, reset_url, expires_at } | null
  const [resetCopied, setResetCopied] = useState(false);
  // Cleanup-report modal state
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const load = async (query, scopeOverride) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if ((query ?? q).trim()) params.set("q", (query ?? q).trim());
      params.set("user_scope", scopeOverride || scope);
      params.set("limit", "100");
      const res = await api.get("/admin/users?" + params.toString());
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("", scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(q, scope), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const openDetail = async (row) => {
    setDetail({ _stub: row });
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/users/${row.user_id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load user");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const suspendToggle = async (row) => {
    const target = !row.suspended;
    const verb = target ? "Suspend" : "Reactivate";
    if (!window.confirm(`${verb} ${row.email}?`)) return;
    setBusyId(row.user_id);
    try {
      await api.post(`/admin/users/${row.user_id}/suspend`, { suspended: target });
      toast.success(target ? "User suspended" : "User reactivated");
      await load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const resetUsage = async (row) => {
    if (!window.confirm(`Reset this month's AI usage for ${row.email}?`)) return;
    setBusyId(row.user_id);
    try {
      const res = await api.post(`/admin/users/${row.user_id}/reset-usage`);
      toast.success(`Cleared ${res.data?.deleted || 0} generations`);
      await load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset usage");
    } finally {
      setBusyId(null);
    }
  };

  const generateResetLink = async (row) => {
    if (!window.confirm(`Generate a one-time password reset link for ${row.email}?\n\nYou'll be able to copy it and send it to the user manually.`)) return;
    setBusyId(row.user_id);
    try {
      const res = await api.post(`/admin/users/${row.user_id}/reset-password-link`);
      setResetLink({
        email: res.data?.email || row.email,
        reset_url: res.data?.reset_url,
        expires_at: res.data?.expires_at,
      });
      setResetCopied(false);
      toast.success("Reset link generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate reset link");
    } finally {
      setBusyId(null);
    }
  };

  const copyResetLink = async () => {
    if (!resetLink?.reset_url) return;
    try {
      await navigator.clipboard.writeText(resetLink.reset_url);
      setResetCopied(true);
      setTimeout(() => setResetCopied(false), 2500);
    } catch (_e) {
      toast.error("Couldn't copy. Select the link and copy manually.");
    }
  };

  const openCleanupReport = async () => {
    setReport({ loading: true });
    setReportLoading(true);
    try {
      const res = await api.get("/admin/users/cleanup-report?limit=1000");
      setReport(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load cleanup report");
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  const downloadReportCsv = () => {
    if (!report?.suspected_test_users?.length) return;
    const header = ["user_id", "email", "name", "created_at", "suspended", "is_test_account_override", "reasons", "has_real_oauth", "has_paid_sub"];
    const rows = report.suspected_test_users.map((r) => [
      r.user_id, r.email || "", r.name || "", r.created_at || "",
      r.suspended ? "true" : "false",
      r.is_test_account_override === null || r.is_test_account_override === undefined ? "" : String(r.is_test_account_override),
      (r.reasons || []).join("|"),
      r.rescue_signals?.has_real_oauth_connection ? "true" : "false",
      r.rescue_signals?.has_paid_subscription ? "true" : "false",
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `postmoro-test-users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const toggleTestFlag = async (row) => {
    // If override is already true (forced test), clear it. Otherwise force-test.
    const override = row.is_test_account_override;
    const next = override === true ? null : true;
    const verb = next === true ? "Mark as test account" : "Restore automatic classification";
    if (!window.confirm(`${verb} for ${row.email}?`)) return;
    setBusyId(row.user_id);
    try {
      await api.post(`/admin/users/${row.user_id}/mark-test`, { is_test_account: next });
      toast.success(next === true ? "Marked as test account" : "Override cleared");
      await load(q, scope);
      // Refresh the cleanup report too if it's open.
      if (report?.summary) await openCleanupReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update flag");
    } finally {
      setBusyId(null);
    }
  };

  const rescueAsReal = async (row) => {
    if (!window.confirm(`Mark ${row.email} as a REAL customer? This overrides the test-pattern detector for this account.`)) return;
    setBusyId(row.user_id);
    try {
      await api.post(`/admin/users/${row.user_id}/mark-test`, { is_test_account: false });
      toast.success("Marked as real customer");
      await load(q, scope);
      if (report?.summary) await openCleanupReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update flag");
    } finally {
      setBusyId(null);
    }
  };

  const grantInternalStarter = async (row) => {
    if (!window.confirm(`Grant Starter access without Stripe billing to this internal QA account?\n\n${row.email}`)) return;
    setBusyId(row.user_id);
    try {
      await api.post(`/admin/users/${row.user_id}/grant-internal-starter`);
      toast.success("Internal Starter granted");
      await load(q, scope);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Could not grant internal Starter";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const removeInternalStarter = async (row) => {
    if (!window.confirm(`Remove this internal Starter entitlement and return the account to Free?\n\n${row.email}`)) return;
    setBusyId(row.user_id);
    try {
      await api.post(`/admin/users/${row.user_id}/remove-internal-starter`);
      toast.success("Internal Starter removed");
      await load(q, scope);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Could not remove internal Starter";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const rows = useMemo(() => data?.items || [], [data]);

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="Customers"
          subtitle={`${data.total.toLocaleString()} ${scope === "real" ? "real" : scope === "test" ? "test" : "total"} accounts · search, suspend, and reset usage.`}
          testidPrefix="admin-customers"
          action={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={openCleanupReport}
                disabled={reportLoading}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="admin-customers-cleanup-report"
              >
                {reportLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ClipboardList className="h-4 w-4 mr-1.5" />}
                Cleanup report
              </Button>
              <Button
                variant="outline"
                onClick={() => load(q, scope)}
                disabled={loading}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="admin-customers-refresh"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                Refresh
              </Button>
            </div>
          }
        />

        <GlassCard className="p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:flex-1 sm:max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[color:var(--text-3)]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by email, name, or user id…"
                className="pl-8 bg-[color:var(--bg-0)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]"
                data-testid="admin-customers-search"
              />
            </div>
            <div className="flex items-center gap-1.5" data-testid="admin-customers-scope-tabs">
              {[
                { v: "real", label: "Real" },
                { v: "test", label: "Test" },
                { v: "all", label: "All" },
              ].map((opt) => {
                const active = scope === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setScope(opt.v)}
                    data-testid={`admin-customers-scope-${opt.v}`}
                    className={
                      "text-xs px-3 py-1.5 rounded-md border transition " +
                      (active
                        ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--text-1)]"
                        : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </GlassCard>

        <GlassCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-customers-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[color:var(--text-3)] border-b border-[color:var(--border-subtle)]">
                  <th className="py-2.5 px-3 font-medium">Customer</th>
                  <th className="py-2.5 px-3 font-medium">Type</th>
                  <th className="py-2.5 px-3 font-medium">Plan</th>
                  <th className="py-2.5 px-3 font-medium">Status</th>
                  <th className="py-2.5 px-3 font-medium">Signup</th>
                  <th className="py-2.5 px-3 font-medium text-right">Posts (mo)</th>
                  <th className="py-2.5 px-3 font-medium">Platforms</th>
                  <th className="py-2.5 px-3 font-medium">Last active</th>
                  <th className="py-2.5 px-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !rows.length ? (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-[color:var(--text-3)]">
                      <Loader2 className="h-4 w-4 inline mr-2 animate-spin" /> Loading…
                    </td>
                  </tr>
                ) : !rows.length ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center">
                      <div className="h-10 w-10 mx-auto rounded-full bg-[color:var(--surface-3)] flex items-center justify-center text-[color:var(--text-2)] mb-2">
                        <Users className="h-4 w-4" />
                      </div>
                      <div className="text-sm text-[color:var(--text-2)]">No customers match this search.</div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.user_id}
                      className="border-b border-[color:var(--border-subtle)] hover:bg-[color:var(--surface-2)] transition-colors"
                      data-testid={`admin-customer-row-${row.user_id}`}
                    >
                      <td className="py-3 px-3">
                        <div className="font-medium text-[color:var(--text-1)] truncate max-w-[220px]">{row.name}</div>
                        <div className="text-[11px] text-[color:var(--text-3)] truncate max-w-[220px]">{row.email}</div>
                      </td>
                      <td className="py-3 px-3"><UserTypeBadge row={row} /></td>
                      <td className="py-3 px-3"><PlanBadge plan={row.plan} planName={row.plan_name} /></td>
                      <td className="py-3 px-3"><StatusBadge status={row.subscription_status} suspended={row.suspended} /></td>
                      <td className="py-3 px-3 text-[color:var(--text-2)]">{fmtDate(row.signup_date)}</td>
                      <td className="py-3 px-3 text-right font-mono">{row.monthly_posts}</td>
                      <td className="py-3 px-3"><PlatformDots items={row.connected_platforms} /></td>
                      <td className="py-3 px-3 text-[color:var(--text-2)] text-[11px]">{fmtRelative(row.last_active)}</td>
                      <td className="py-3 px-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              disabled={busyId === row.user_id}
                              data-testid={`admin-customer-menu-${row.user_id}`}
                            >
                              {busyId === row.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]"
                          >
                            <DropdownMenuItem onClick={() => openDetail(row)} data-testid={`admin-customer-view-${row.user_id}`}>
                              <Eye className="h-3.5 w-3.5 mr-2" /> View user
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => resetUsage(row)} data-testid={`admin-customer-reset-${row.user_id}`}>
                              <RotateCcw className="h-3.5 w-3.5 mr-2" /> Reset usage
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => generateResetLink(row)} data-testid={`admin-customer-pw-reset-${row.user_id}`}>
                              <KeyRound className="h-3.5 w-3.5 mr-2" /> Generate password reset link
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-[color:var(--border-subtle)]" />
                            {row.is_test_account_override === false || row.user_type === "real" ? (
                              <DropdownMenuItem onClick={() => toggleTestFlag(row)} data-testid={`admin-customer-mark-test-${row.user_id}`}>
                                <FlaskConical className="h-3.5 w-3.5 mr-2" /> Mark as test account
                              </DropdownMenuItem>
                            ) : null}
                            {row.user_type === "test" ? (
                              <DropdownMenuItem onClick={() => rescueAsReal(row)} data-testid={`admin-customer-mark-real-${row.user_id}`}>
                                <Sparkles className="h-3.5 w-3.5 mr-2 text-[color:var(--success)]" /> Mark as real customer
                              </DropdownMenuItem>
                            ) : null}
                            {row.is_test_account_override === true ? (
                              <DropdownMenuItem onClick={() => toggleTestFlag(row)} data-testid={`admin-customer-clear-override-${row.user_id}`}>
                                <RotateCcw className="h-3.5 w-3.5 mr-2" /> Clear test override
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuSeparator className="bg-[color:var(--border-subtle)]" />
                            {row.entitlement_source === "internal_test" && row.manual_entitlement ? (
                              <DropdownMenuItem
                                onClick={() => removeInternalStarter(row)}
                                data-testid={`admin-customer-remove-internal-starter-${row.user_id}`}
                              >
                                <XCircle className="h-3.5 w-3.5 mr-2" /> Remove Internal Starter
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => grantInternalStarter(row)}
                                data-testid={`admin-customer-grant-internal-starter-${row.user_id}`}
                              >
                                <Gift className="h-3.5 w-3.5 mr-2 text-[color:var(--info)]" /> Grant Internal Starter
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator className="bg-[color:var(--border-subtle)]" />
                            {row.suspended ? (
                              <DropdownMenuItem
                                onClick={() => suspendToggle(row)}
                                className="text-[color:var(--success)] focus:text-[color:var(--success)]"
                                data-testid={`admin-customer-unsuspend-${row.user_id}`}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5 mr-2" /> Reactivate user
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => suspendToggle(row)}
                                className="text-[color:var(--danger)] focus:text-[color:var(--danger)]"
                                data-testid={`admin-customer-suspend-${row.user_id}`}
                              >
                                <Ban className="h-3.5 w-3.5 mr-2" /> Suspend user
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
      </div>

      {/* User detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent
          className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] max-w-2xl"
          data-testid="admin-user-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle>User detail</DialogTitle>
          </DialogHeader>
          {detailLoading || detail?._stub ? (
            <div className="py-6 text-center text-[color:var(--text-3)]">
              <Loader2 className="h-4 w-4 inline mr-2 animate-spin" /> Loading…
            </div>
          ) : detail?.user ? (
            <div className="space-y-4 mt-1">
              <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-semibold text-[color:var(--text-1)] truncate">{detail.user.name || "—"}</div>
                    <div className="text-xs text-[color:var(--text-3)] truncate">{detail.user.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <PlanBadge plan={detail.subscription?.plan} planName={detail.subscription?.plan} />
                    <StatusBadge status={detail.subscription?.status} suspended={detail.user.suspended} />
                  </div>
                </div>
                <div className="text-[11px] font-mono text-[color:var(--text-3)] mt-2">{detail.user.user_id}</div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Posts total" value={detail.stats?.posts_total} />
                <Stat label="This month" value={detail.stats?.posts_this_month} />
                <Stat label="Published" value={detail.stats?.posts_published} />
                <Stat label="Failed" value={detail.stats?.posts_failed} />
              </div>

              <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3">
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)] mb-2">Connections</div>
                {detail.connections?.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {detail.connections.map((c) => (
                      <span
                        key={c.platform}
                        className="inline-flex items-center gap-1 rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--bg-0)] px-2 py-0.5 text-[11px] text-[color:var(--text-2)]"
                      >
                        {c.platform}
                        {c.connected ? <span className="text-[10px] text-[color:var(--success)]">· {c.mode || "real"}</span> : <span className="text-[10px] text-[color:var(--text-3)]">· off</span>}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-[color:var(--text-3)]">No social connections yet.</div>
                )}
              </div>

              <div className="text-[11px] text-[color:var(--text-3)]">
                Joined {fmtDate(detail.user.created_at)} · Last active {fmtRelative(detail.stats?.last_active)}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Password-reset-link result dialog */}
      <Dialog open={!!resetLink} onOpenChange={(o) => { if (!o) { setResetLink(null); setResetCopied(false); } }}>
        <DialogContent
          className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] max-w-xl"
          data-testid="admin-reset-link-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-semibold flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-[color:var(--brand-primary)]" /> One-time password reset link
            </DialogTitle>
          </DialogHeader>
          {resetLink ? (
            <div className="space-y-3 mt-1">
              <div className="text-sm text-[color:var(--text-2)]">
                Share this link with <span className="font-medium text-[color:var(--text-1)]">{resetLink.email}</span>. They can use it once to set a new password.
              </div>
              <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5 flex items-center gap-2">
                <code className="flex-1 text-[11px] font-mono break-all text-[color:var(--text-1)]" data-testid="admin-reset-link-url">{resetLink.reset_url}</code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyResetLink}
                  className="shrink-0 border-[color:var(--border-subtle)] bg-[color:var(--bg-0)] text-[color:var(--text-1)]"
                  data-testid="admin-reset-link-copy"
                >
                  {resetCopied ? <><Check className="h-3.5 w-3.5 mr-1.5" />Copied</> : <><Copy className="h-3.5 w-3.5 mr-1.5" />Copy</>}
                </Button>
              </div>
              {resetLink.expires_at ? (
                <div className="text-[11px] text-[color:var(--text-3)]">
                  Expires {fmtDate(resetLink.expires_at)}. Single-use only.
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Cleanup report dialog — read-only audit, no bulk delete */}
      <Dialog open={!!report} onOpenChange={(o) => { if (!o) setReport(null); }}>
        <DialogContent
          className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] max-w-3xl"
          data-testid="admin-cleanup-report-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-semibold flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-[color:var(--brand-primary)]" /> Test account cleanup report
            </DialogTitle>
          </DialogHeader>
          {report?.loading ? (
            <div className="py-6 text-center text-[color:var(--text-3)]">
              <Loader2 className="h-4 w-4 inline mr-2 animate-spin" /> Auditing accounts…
            </div>
          ) : report?.summary ? (
            <div className="space-y-3 mt-1" data-testid="admin-cleanup-report-body">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Real users" value={report.summary.real} />
                <Stat label="Test users" value={report.summary.test} />
                <Stat label="Admin" value={report.summary.admin} />
              </div>
              <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-[11px] text-[color:var(--text-2)] flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-[color:var(--warm)] mt-0.5 shrink-0" />
                <span>
                  Read-only report. No accounts are deleted or modified. Use the row menu in this table to manually mark a user as test or real, or to permanently delete via the user-detail flow once you confirm the action.
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-[color:var(--text-3)]" data-testid="admin-cleanup-report-count">
                  Showing {report.suspected_test_users?.length || 0} of {report.summary.test.toLocaleString()} suspected test accounts.
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadReportCsv}
                  disabled={!report.suspected_test_users?.length}
                  className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                  data-testid="admin-cleanup-report-csv"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />Export CSV
                </Button>
              </div>
              <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--bg-0)] overflow-hidden max-h-[360px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[color:var(--surface-2)]">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[color:var(--text-3)] border-b border-[color:var(--border-subtle)]">
                      <th className="py-2 px-2.5 font-medium">Email</th>
                      <th className="py-2 px-2.5 font-medium">Reasons</th>
                      <th className="py-2 px-2.5 font-medium">Rescue?</th>
                      <th className="py-2 px-2.5 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.suspected_test_users?.map((r) => (
                      <tr key={r.user_id} className="border-b border-[color:var(--border-subtle)] last:border-0" data-testid={`admin-cleanup-row-${r.user_id}`}>
                        <td className="py-1.5 px-2.5 text-[color:var(--text-1)] font-mono text-[11px] break-all">{r.email}</td>
                        <td className="py-1.5 px-2.5 text-[color:var(--text-2)] text-[10px]">{(r.reasons || []).join(", ") || "—"}</td>
                        <td className="py-1.5 px-2.5 text-[10px]">
                          {r.rescue_signals?.has_real_oauth_connection ? (
                            <span className="text-[color:var(--success)]">OAuth</span>
                          ) : r.rescue_signals?.has_paid_subscription ? (
                            <span className="text-[color:var(--success)]">Paid</span>
                          ) : (
                            <span className="text-[color:var(--text-3)]">—</span>
                          )}
                        </td>
                        <td className="py-1.5 px-2.5 text-[color:var(--text-3)] text-[10px]">{fmtDate(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// User-type badge shown in the customers table. Color-coded for instant scan.
function UserTypeBadge({ row }) {
  const t = row?.user_type || "real";
  const reasons = row?.test_email_reasons || [];
  const override = row?.is_test_account_override;
  const map = {
    real:  { label: "Real",  cls: "bg-[color:rgba(67,170,139,0.12)] text-[color:var(--success)] border-[color:rgba(67,170,139,0.30)]" },
    test:  { label: "Test",  cls: "bg-[color:rgba(232,166,90,0.12)] text-[color:var(--warm)] border-[color:rgba(232,166,90,0.30)]" },
    admin: { label: "Admin", cls: "bg-[color:rgba(110,166,238,0.12)] text-[color:var(--info)] border-[color:rgba(110,166,238,0.30)]" },
  };
  const cfg = map[t] || map.real;
  const tooltip = override === true
    ? "Manually marked as test by admin override"
    : override === false
      ? "Manually marked as real by admin override"
      : reasons.length
        ? `Auto-detected as test (matched: ${reasons.join(", ")})`
        : t === "admin" ? "Listed in ADMIN_EMAILS allow-list" : "No test patterns matched";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cfg.cls}`}
      title={tooltip}
      data-testid={`admin-user-type-${t}-${row?.user_id}`}
    >
      {cfg.label}
      {override !== null && override !== undefined ? <span className="text-[8px] opacity-70 ml-0.5">override</span> : null}
    </span>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">{label}</div>
      <div className="font-display text-lg font-semibold text-[color:var(--text-1)]">{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}
